import { TreeViewBaseItem } from "@mui/x-tree-view";
import { orderBy } from "../../../utils/jsUtils";

const sortTreeItems = (items: TreeViewBaseItem[]): TreeViewBaseItem[] => {
  return orderBy(
    items,
    [(item: TreeViewBaseItem) => item.label.split(":")[0].trim()],
    ["asc"]
  ).map((item: TreeViewBaseItem) => ({
    ...item,
    children: item.children ? sortTreeItems(item.children) : [],
  }));
};

export const transformToTree = <T extends { id: string; parentId: string }>(
  data: T[],
  labelFormatter: (item: T) => string
): TreeViewBaseItem[] => {
  const itemsMap = new Map<string, TreeViewBaseItem>();

  // First pass: create all items
  for (const item of data) {
    itemsMap.set(item.id, {
      id: item.id,
      label: labelFormatter(item),
      children: [],
    });
  }

  // Second pass: build the tree structure
  const rootItems: TreeViewBaseItem[] = [];

  for (const item of data) {
    const treeItem = itemsMap.get(item.id);
    if (!treeItem) continue;

    if (item.parentId === "0") {
      rootItems.push(treeItem);
    } else {
      const parent = itemsMap.get(item.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(treeItem);
      }
    }
  }

  // Sort the tree recursively
  return sortTreeItems(rootItems);
};

/**
 * Recursively searches through all items and their children for matches.
 * Returns a new tree containing only the matching items and their ancestors.
 */
export const searchItems = (
  items: TreeViewBaseItem[],
  searchValue: string
): TreeViewBaseItem[] => {
  if (!searchValue.trim()) return items;

  const searchLower = searchValue.toLowerCase();

  return items.reduce<TreeViewBaseItem[]>((result, item) => {
    // Check if this item matches
    const matches =
      item.label && item.label.toString().toLowerCase().includes(searchLower);

    // Check if any children match
    const matchingChildren = item.children
      ? searchItems(item.children, searchValue)
      : [];

    // If this item matches or has matching children, include it in the result
    if (matches || matchingChildren.length > 0) {
      result.push({
        ...item,
        children: matchingChildren,
      });
    }

    return result;
  }, []);
};

/**
 * Extracts all item IDs that should be expanded when search results are displayed.
 * This includes all parent nodes that have matching descendants.
 */
export const getExpandedItemIds = (items: TreeViewBaseItem[]): string[] => {
  const expandedIds: string[] = [];

  const collectParentIds = (items: TreeViewBaseItem[]): void => {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        // Add this item's ID to expanded list since it has children
        expandedIds.push(item.id);
        // Recursively collect children's IDs
        collectParentIds(item.children);
      }
    }
  };

  collectParentIds(items);
  return expandedIds;
};
